export type PrimaryActionLinkType =
   | 'courseDetails'
   | 'lessonDetails'
   | 'bundleDetails'
   | 'bundleIndex'
   | 'premiumSubscriptionDetails'
   | 'subscriptionPlansIndex'
   | 'siteWideSubscriptionsIndex';

export type PrimaryAction = {
   link_type: PrimaryActionLinkType;
   slug?: string;
   params?: {
      lessonSlug?: string;
   };
   // Server-computed CTA button label (e.g. "Free for members" / "Free for
   // subscribers" / "View Subscription"). Optional here for backward
   // compatibility; the API always sends it, and consumers fall back to the
   // build-time constant when it is absent.
   text?: string;
};

// Server-computed copy for the global subscription CTA modal (per portal).
// The member variant is returned when the portal offers a granting membership;
// the subscriber variant otherwise. buttonLinkType is a semantic key the
// frontend maps to a route (see resolveSubscriptionCtaModal).
export type SubscriptionCtaModalButtonLinkType = 'subscribe' | 'memberships';

export type SubscriptionCtaModalCopy = {
   title: string;
   message: string;
   buttonText: string;
   buttonLinkType: SubscriptionCtaModalButtonLinkType;
};

export type SubscriptionCtaModal = {
   course: SubscriptionCtaModalCopy;
   blog: SubscriptionCtaModalCopy;
};

export type LessonTableField =
   | 'contributor'
   | 'level'
   | 'categories'
   | 'progress'
   | 'release_date'
   | 'status';

export type LessonsTableUiConfig = {
   visible_fields: LessonTableField[];
   configuration: {
      contributor: boolean;
      level: boolean;
      categories: boolean;
      progress: boolean;
      release_date: boolean;
      status: boolean;
   };
};

type AdminPreview = {
   admin_preview?: boolean;
};

export type CourseUiConfig = {
   lessons_table?: LessonsTableUiConfig;
};

export type Course = {
   id?: number;
   image_url: string;
   title: string;
   lessons_count: number;
   lesson_count_by_contributor: {
      contributor: Contributor;
      lesson_count: number;
   }[];
   slug: string;
   first_lesson_slug: string;
   should_link_to_first_lesson: boolean;
   description: string;
   short_description: string;
   promo_video_url?: string;
   lessons: Lesson[] | undefined;
   contributors: Contributor[];
   level: LmsCourseLevel;
   access_level: CourseAccessLevel;
   course_completion_percent: number;
   admin_preview?: boolean;
   content_type: 'course' | 'single_bundle';
   course_price?: number | null;
   course_price_id?: string | null;
   is_locked?: boolean;
   bundle_info?: CourseBundleInfo | null;
   available_bundles?: PremiumBundleInfo[];
   first_bundle_slug?: string | null;
   should_link_to_first_bundle?: boolean;
   subscription_plans?: CourseSubscriptionPlan[];
   badge_info?: CourseBadgeInfo;
   badge?: {
      type:
         | 'free'
         | 'site-subscription'
         | 'course-subscription'
         | 'premium-package'
         | 'purchased';
      // Server-computed badge chip label (e.g. "Members" / "Subscribe").
      // Optional/null for backward compatibility; consumers fall back to the
      // build-time constant per badge type when absent.
      label?: string | null;
   } | null;
   is_admin_preview_user?: boolean;
   published?: boolean;
   primary_action?: PrimaryAction;
   ui?: CourseUiConfig;
   course_categories?: NestedCourseCategory[];
};

export type CourseBundleInfo = {
   plan_title: string;
   price: number;
   price_id: string;
   billing_interval: string;
   button_text: string;
   show_best_value_badge: boolean;
   priority_order: number;
   image_url: string;
   plan_description: string;
   short_description: string;
   promo_video_url?: string;
};

export type PremiumBundleInfo = {
   id: number;
   plan_title: string;
   page_slug: string;
   price: number;
   priority_order: number;
};

export type BundleLesson = {
   title: string;
   slug: string;
   thumbnail_url: string;
   admin_preview?: boolean;
   // Enhanced fields for purchased bundles
   lesson_url?: string;
};

export type BundleCourse = {
   id: number;
   title: string;
   lessons?: BundleLesson[];
   image_url?: string;
   description?: string;
   short_description?: string;
   contributors?: Contributor[];
   // Enhanced fields for purchased bundles
   slug?: string;
   access_level?: CourseAccessLevel;
   purchased_status?: string;
   course_url?: string;
   lessons_count?: number;
   course_completion_percent?: number | null;
   first_lesson_slug?: string;
   should_link_to_first_lesson?: boolean;
   available_bundles?: Array<{
      id: number;
      plan_title: string;
      page_slug: string;
      price: number;
      priority_order: number;
   }>;
   should_link_to_first_bundle?: boolean;
   first_bundle_slug?: string | null;
   content_type?: string;
   bundle_info?: any;
};

export type PremiumBundle = {
   id: number;
   plan_title: string;
   plan_description: string | null;
   description?: string | null; // Alias for plan_description
   short_description?: string;
   price: string;
   billing_interval: string;
   plan_status: string;
   page_slug: string;
   promo_video_url?: string;
   button_text: string | null;
   show_best_value_badge: boolean;
   priority_order: number;
   image_url?: string;
   courses: BundleCourse[];
   stripe_price_id?: string;
   purchased_at?: string | null;
   access_url?: string | null;
};

export type Contributor = {
   id: string;
   name: string;
   image_url: string;
   slug: string;
   lesson_count?: number;
   short_bio?: string;
   long_bio?: string;
   professional_title?: string;
   courses_count: number;
   featured_bio?: string;
   enable_coaching_session_buttons?: boolean;
} & AccessInfo;

export type Instructor = {
   name: string;
   skillTitle: string;
};

export type H5pCompletionInfo = {
   activity_file_name: string;
   completed_at: string | null;
   submitted_at: string | null;
   score_info: {
      passing_percentage: number;
      minimum_score_required: number;
      actual_score: number;
      readable_status: string;
   };
};

export type LessonCategory = {
   id: number;
   title: string;
   slug: string;
};

export type LessonLevel = {
   id: number;
   title: 'Introductory' | 'Beginner' | 'Intermediate' | 'Advanced';
   level: number;
   slug?: string;
};

export type VideoCredit = {
   name: string;
   logo_url: string;
   website_url?: string;
};

export type VideoCredits = Record<string, VideoCredit>;

export type VideoCategory = {
   id: number;
   title: string;
   slug: string;
   poster_url: string | null;
   video_count: number;
   sort_order: number;
};

export type Video = {
   id: number;
   title: string;
   slug: string;
   poster_url: string | null;
   src: string;
   duration_seconds: number | null;
   sort_order: number;
   credits: VideoCredit | null;
   is_premium: boolean;
   has_access: boolean;
};

export type VideoAnalyticsMilestones = {
   quarter_25: boolean;
   quarter_50: boolean;
   quarter_75: boolean;
   quarter_100: boolean;
};

export type VideoAnalyticsPayload = {
   session_id: string;
   video_src: string;
   video_duration: number;
   page_path: string;
   watched_duration: number;
   max_timestamp: number;
   milestones: VideoAnalyticsMilestones;
   pause_count: number;
   seek_count: number;
   started_at: string | null;
   completed_at: string | null;
   sent_at: string;
};

export type Lesson = {
   id: number;
   title: string;
   slug: string;
   image_url?: string;
   accordion: string;
   intro: string;
   summary: string;
   transcript: string;
   knowledge_check: string;
   contributor?: Contributor;
   thumbnail_url: string;
   courses: Course[];
   survey_completed_at?: string;
   admin_preview?: boolean;
   lesson_completion_percent?: number;
   activities_status: H5pCompletionInfo[];
   release_date?: string;
   is_admin_preview_user?: boolean;
   is_released?: boolean;
   level?: LessonLevel | null;
   lesson_categories?: LessonCategory[];
   curriculum_order?: number;
   released_position?: number;
   status?: string;
   video_credits?: VideoCredits;
} & withDisclaimer;

export type LmsHeadingTitle = {
   textContent: string;
   tagType: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

/**
 * Access info for any type of record, only available to admins
 */
export type AccessInfo = {
   access_info?: {
      access_mode: 'include' | 'exclude';
      portals: string[];
   };
};

export type CourseCategory = {
   id: string;
   title: string;
   slug: string;
   image_url: string;
   description: string;
   courses_count: number;
   parent_category_id?: string | null;
   child_categories: {
      title: string;
      slug: string;
   }[];
   admin_preview?: boolean;
   /**
    * Hide category in category index listing from customers but show it to admins
    */
   hidden?: boolean;
} & AccessInfo;

/**
 * Nested course category structure returned by the API
 * Used in course responses for hierarchical category display
 */
export type NestedCourseCategory = {
   id: number;
   title: string;
   slug: string;
   published: boolean;
   image_url: string | null;
   description: string | null;
   parent_category_id: number | null;
   isChild: boolean;
   children: NestedCourseCategory[];
};

export type LmsCourseLevel = {
   id: string;
   title: 'Introductory' | 'Beginner' | 'Intermediate' | 'Advanced';
   level: string;
   slug: 'introductory' | 'beginner' | 'intermediate' | 'advanced';
};

export enum CourseAccessLevel {
   Public = 'Public',
   PubliclyListed = 'Publicly Listed',
   Premium = 'Premium',
   IPASubscription = 'IPA Subscription',
   PremiumSubscription = 'Premium Subscription',
}

export enum CourseAudience {
   Player = 'Player',
   Instructor = 'Coach / Instructor',
   Umpire = 'Umpire',
   Team = 'Team',
   'I/I' = 'I/I',
}

export enum CourseLevel {
   Introductory = 'Introductory',
   Beginner = 'Beginner',
   Intermediate = 'Intermediate',
   Advanced = 'Advanced',
}

export enum UserTitle {
   Mr = 'Mr',
   Ms = 'Ms',
   Mrs = 'Mrs',
   Dr = 'Dr',
   Prof = 'Prof',
   Sr = 'Sr',
   Jr = 'Jr',
}

export type OptionsCourseCategory = {
   id: string;
   parent_category_id: string;
   title: string;
   children: OptionsCourseCategory[];
};

export type SearchFieldsOptions = {
   contributors?: {
      id: string;
      name: string;
   }[];
   courseCategories?: OptionsCourseCategory[];
   courseLevels?: {
      id: string;
      title: string;
   }[];
   difficultyRatings?: {
      value: string;
      label: string;
   }[];
   rateOptions?: {
      value: number;
      label: string;
   }[];
   courseTitle?: string;
   freeContent?: number;
   audience?: CourseAudience[];
   page?: number;
};

export type AccessType =
   | 'free'
   | 'site-subscription'
   | 'course-subscription'
   | 'premium-package'
   | 'purchased';

export type ApiSearchRequestParams = {
   contributors?: string[];
   // category?: SearchFieldsOptions['category'];
   courseCategories?: string[];
   courseLevels?: string[];
   courseTitle?: string;
   page?: number;
   audiences?: string[];
   accessTypes?: AccessType[];
   sortBy?:
      | 'title'
      | 'published_lessons_count'
      | 'level'
      | 'course_completion_percent'
      | 'access_type';
   sortOrder?: 'asc' | 'desc';
   freeContent?: number;
};

export type MembershipPlanActionType =
   | 'checkoutSubscription'
   | 'manageSubscription'
   | 'submitApplication'
   | 'membershipDetails';

export interface MembershipPlanPrimaryAction {
   action_type: MembershipPlanActionType;
   slug?: string;
}

export type FormSchemaFieldType = 'text' | 'textarea' | 'email' | 'number' | 'date' | 'select' | 'radio' | 'checkbox';

export interface FormSchemaField {
   name: string;
   type: FormSchemaFieldType;
   label: string;
   required?: boolean;
   placeholder?: string;
   options?: string[];
   max_length?: number;
}

export interface MembershipPlan {
   id: number;
   plan_title: string;
   billing_interval: 'monthly' | 'yearly';
   price: string;
   display_price: string;
   page_slug: string;
   description: string | null;
   short_description: string | null;
   image_url: string | null;
   show_best_value_badge: boolean;
   duration_text: string;
   priority_order: number;
   stripe_price_id: string;
   plan_status: 'Published' | 'Admin Preview' | 'Deprecated';
   button_text: string;
   checkout_url: string;
   is_active: boolean;
   paid_thru: string | null;
   requires_approval: boolean;
   collect_member_details: boolean;
   primary_action?: MembershipPlanPrimaryAction;
   form_schema?: FormSchemaField[];
}

export interface ActiveMembership extends MembershipPlan {
   subscription_id: string;
   subscription_status: string;
   current_period_start: number;
   current_period_end: number;
   printable_current_period_start: string;
   printable_current_period_end: string;
}

export interface PaginatedResults<ItemType> {
   data: ItemType[];
   links: {
      first: string | null;
      last: string | null;
      prev: string | null;
      next: string | null;
   };
   meta: {
      current_page: number;
      from: number;
      last_page: number;
      per_page: number;
      to: number;
      total: number;
      available_filters?: {
         years: number[];
         seasons: string[];
      };
   };
}

export interface MembershipPlansResponse extends PaginatedResults<MembershipPlan> {
   has_membership_terms: boolean;
}

export interface MembershipTerms {
   title: string;
   body: string;
   use_custom_content: boolean;
}

export enum LmsRouteNames {
   home = 'LmsHome',
   courseCategories = 'LmsCourseCategories',
   courseCategory = 'LmsCourseCategory',
   lesson = 'LmsLesson',
   allContent = 'LmsAllContent',
   byLevel = 'LmsByLevel',
   levelCourses = 'LmsLevelCourses',
   subscribe = 'LmsSubscribe',
   profile = 'LmsProfile',
   subscriptions = 'LmsSubscriptions',
   premiumPackages = 'LmsPremiumPackages',
   courseDetails = 'LmsCourseDetails',
   contributors = 'LmsContributors',
   contributorDetails = 'LmsContributorDetails',
   login = 'LmsLogin',
   /**
    * Magic links login, referring club / tracking
    */
   brandLogin = 'BrandLogin',
   /**
    * Packaged-app pre-login screen. Unreachable on website builds — see
    * functions/resolveLoginEntryPath.ts.
    */
   appWelcome = 'AppWelcome',
   notfound = 'LmsNotfound',
   lmsPlayLearn = 'LmsPlayLearn',
   premium = 'LmsPremium',
   premiumDetails = 'LmsPremiumDetails',
   courseBundleSelection = 'LmsCourseBundleSelection',
   courseSubscription = 'LmsCourseSubscription',
   premiumSubscriptions = 'LmsPremiumSubscriptions',
   premiumSubscriptionDetails = 'LmsPremiumSubscriptionDetails',
   magazine = 'lms-magazine',
   blogHome = 'lms-blog-home',
   blogPost = 'lms-blog-post',
   blogCategory = 'lms-blog-category',
   tournaments = 'lms-tournaments',
   chukkers = 'lms-chukkers',
   video = 'LmsVideo',
   videoCategory = 'LmsVideoCategory',
   gallery = 'lms-gallery',
   galleryDetail = 'lms-gallery-detail',
   memberships = 'LmsMemberships',
   membershipDetails = 'LmsMembershipDetails',
   clubListings = 'LmsClubListings',
   leaderboard = 'LmsLeaderboard',
   customPage = 'lms-custom-page',
}

export enum SiteRouteNames {
   home = 'WebsiteHome',
   platform = 'WebsitePlatform',
   about = 'WebsiteAbout',
   work = 'WebsiteWork',
   contact = 'WebsiteContact',
   events = 'WebsiteEvents',
   internationalPoloAcademy = 'WebsiteInternationalPoloAcademy',
   courses = 'WebsiteCourses',
   blog = 'WebsiteBlog',
   premiumPackages = 'WebsitePremiumPackages',
   premiumSubscriptions = 'WebsitePremiumSubscriptions',
   subscriptions = 'WebsiteSubscriptions',
   performanceAnalysis = 'WebsitePerformanceAnalysis',
}

export type FeaturedContentResponse = {
   data: {
      contributors: Contributor[];
   };
};

// Course bundles API response types for multiple bundle selection
export type CourseSubscriptionPlan = {
   id: number;
   plan_title: string;
   billing_interval: 'monthly' | 'yearly';
   price: number;
   stripe_price_id: string;
   page_slug: string;
   button_text: string;
   plan_status: string;
   priority_order: number;
   display_price: string;
   trial_days?: number;
   savings_percentage?: number;
};

export type CourseBadgeInfo = {
   badge_type:
      | 'free'
      | 'premium-package'
      | 'site-subscription'
      | 'course-subscription';
   display_price?: string | null;
   primary_action_text: string;
};

export type CourseBundlesApiResponse = {
   course: {
      id: number;
      title: string;
      slug: string;
      image_url: string;
      promo_video_url: string;
      description: string;
      lessons_count: number;
      contributors: Array<{
         id: number;
         name: string;
         slug: string;
      }>;
      level: {
         id: number;
         title: string;
         level: number;
      };
   };
   bundles: Array<{
      id: number;
      plan_title: string;
      page_slug: string;
      price: string;
      billing_interval: string;
      plan_description: string;
      short_description: string;
      image_url: string;
      show_best_value_badge: boolean;
      priority_order: number | null;
      button_text: string;
      courses_count: number;
      courses: Array<{
         id: number;
         title: string;
         image_url: string;
      }>;
   }>;
   meta: {
      total_bundles: number;
      price_range: {
         min: number;
         max: number;
         currency: string;
      };
   };
};

export type Magazine = {
   id: number;
   title: string;
   year: number;
   season: 'spring' | 'summer' | 'autumn' | 'winter';
   issuu_embed_url: string;
   thumbnail_url: string;
   created_at: string;
   updated_at: string;
};

/* Use this type for entities that can display a disclaimer section like blog posts or lessons **/
type withDisclaimer = {
   has_disclaimer?: boolean;
};

export interface BlogPostCategory {
   title: string;
   slug: string;
}

export type BlogPost = {
   date: string;
   title: string;
   slug: string;
   excerpt?: string;
   body?: string;
   admin_preview?: boolean;
   has_access?: boolean;
   thumbnail_url?: string;
   categories?: BlogPostCategory[];
   video_credits?: VideoCredits;

   related_posts?: BlogPost[];
} & withDisclaimer;

export interface PublicCourseLevel {
   id: number;
   title: string;
   slug: string;
}

export interface PublicCourse {
   id: number;
   title: string;
   slug: string;
   image_url: string | null;
   description: string;
   short_description?: string;
   lessons_count: number;
   level: PublicCourseLevel | null;
}

export interface PublicSubscriptionPlan {
   id: number;
   plan_title: string;
   billing_interval: 'one-time' | 'monthly' | 'yearly';
   price: string;
   display_price: string;
   page_slug: string;
   description: string;
   short_description: string;
   show_best_value_badge: boolean;
   image_url: string | null;
   promo_video_url: string | null;
   priority_order: number | null;
   courses: PublicCourse[];
   courses_count: number;
}

// Tournament types
export interface TournamentLineupPlayer {
   name: string;
   handicap: string;
   photo?: string | null;
   country?: string | null;
}

export interface TournamentLineup {
   team: string;
   teamHandicap: string;
   players: TournamentLineupPlayer[];
}

export interface ClubInfo {
   name: string;
   address?: string | null;
   coordinates?: GameGroundCoordinates | null;
   directions_url?: string | null;
}

export type ClubListing = {
   name: string;
   address: string;
   manager: string;
   lessons: boolean;
   fields: number;
   phone: string;
};

/**
 * An external payment page for a tournament's entry fee, owned by the organiser rather
 * than by us. There is no Checkout Session and no webhook behind it - the whole feature
 * is a validated URL carried from the admin to the browser.
 *
 * The block is null unless a URL is configured, so `url` is never null when the object
 * exists and presence checking is just `if (payment)`. Optional fields normalise to null
 * rather than empty string, so `payment.label || 'Pay Entry Fee'` is safe.
 */
export interface TournamentEntryPayment {
   url: string;
   // CTA text override. Null means use our default.
   label: string | null;
   // Display text only, never parsed - the fee structure varies (per team, per player,
   // early bird) and there is no numeric field behind it.
   amount_label: string | null;
   // True when payment is mandatory to be admitted.
   required: boolean;
   // Plain text by contract, stripped on write and on read. Never render with v-html.
   note: string | null;
}

export interface TournamentEntryBlock {
   accepts_entries: boolean;
   // Date-only (YYYY-MM-DD). The API resolves it to the end of that day, so the
   // advertised date is the last day entries are accepted.
   registration_closes_at: string | null;
   is_closed: boolean;
   // Optional for deploy-order safety. The API always sends the key, null when no
   // payment link is configured for the tournament.
   payment?: TournamentEntryPayment | null;
}

export interface Tournament {
   id: number;
   tournament: string;
   club: ClubInfo | string | null;
   country: string;
   countryFlag: string;
   tournamentHandicap: string;
   startDate: string;
   // Null whenever the tournament has no end date set, which is a real state in the data.
   endDate: string | null;
   // Count of games attached to the tournament, supplied by the list endpoint so the UI
   // can tell up front whether the games panel would be empty. Nullable on the wire.
   games_count: number | null;
   teams_count?: number;
   hasLineups?: boolean;
   initialLineups?: TournamentLineup[];
   entry?: TournamentEntryBlock | null;
}

export interface TournamentCountryOption {
   label: string;
   value: string;
   flag: string;
}

export interface TournamentFilterOptions {
   available_seasons: number[];
   available_clubs: string[];
   available_countries: TournamentCountryOption[];
   available_tournaments: string[];
}

export interface TournamentFilterOptionsResponse {
   data: TournamentFilterOptions;
}

export interface TournamentDetails extends Tournament {
   googleMapsLink: string | null;
   tournamentDetailsLink: string | null;
}

export interface TournamentDetailsResponse {
   data: TournamentDetails;
}

export interface TournamentsResponse {
   data: Tournament[];
   meta: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
   };
}

// Tournament Entry form types
export type TournamentEntryFieldType =
   | 'text'
   | 'textarea'
   | 'email'
   | 'number'
   | 'date'
   | 'select'
   | 'radio'
   | 'multiselect'
   | 'checkbox'
   | 'team_roster'
   | 'signature'
   | 'html_content';

export interface TournamentEntryFieldBase {
   name: string;
   type: TournamentEntryFieldType;
   label?: string;
   required?: boolean;
   placeholder?: string;
}

export interface TournamentEntryFieldInput extends TournamentEntryFieldBase {
   type: 'text' | 'textarea' | 'email' | 'number' | 'date';
}

export interface TournamentEntryFieldChoice extends TournamentEntryFieldBase {
   type: 'select' | 'radio' | 'multiselect' | 'checkbox';
   options?: string[];
}

export interface TournamentEntryRosterColumn {
   key: 'first_name' | 'last_name' | 'handicap';
   label: string;
   required: boolean;
   maxlength?: number;
}

export interface TournamentEntryFieldTeamRoster extends TournamentEntryFieldBase {
   type: 'team_roster';
   min_players: number;
   max_players: number;
   default_player_count: number;
   // Per-column required-ness/labels come from the API. Optional so older
   // cached responses without `columns` still type-check; the component falls
   // back to hardcoded defaults when it's absent.
   columns?: TournamentEntryRosterColumn[];
}

export interface TournamentEntryFieldSignature extends TournamentEntryFieldBase {
   type: 'signature';
}

export interface TournamentEntryFieldHtmlContent {
   type: 'html_content';
   html: string;
}

export type TournamentEntryFormField =
   | TournamentEntryFieldInput
   | TournamentEntryFieldChoice
   | TournamentEntryFieldTeamRoster
   | TournamentEntryFieldSignature
   | TournamentEntryFieldHtmlContent;

export interface TournamentEntryFormTournament {
   tournament_id: number;
   name: string;
   startDate: string | null;
   endDate: string | null;
   registration_closes_at: string | null;
   // Handicap as the "N-Goal" label, matching the tournaments list endpoint. Optional
   // rather than required so the page still renders against an API that predates it.
   tournamentHandicap?: string | null;
   // Host club, in the same shape the tournaments list endpoint returns.
   club?: ClubInfo | string | null;
   // Closed tournaments are filtered out of this endpoint entirely, so a payment block
   // here always belongs to a tournament that was open at the time the form was loaded.
   payment?: TournamentEntryPayment | null;
}

export interface TournamentEntryFormResponse {
   title: string;
   form_schema: TournamentEntryFormField[];
   tournaments: TournamentEntryFormTournament[];
}

export interface TournamentEntryRosterRow {
   first_name: string;
   last_name: string;
   handicap: string;
}

export type TournamentEntryFieldValue =
   | string
   | number
   | boolean
   | string[]
   | TournamentEntryRosterRow[];

export interface TournamentEntrySubmitPayload {
   tournament_ids: number[];
   submitter_email: string;
   submitter_phone?: string;
   team_name: string;
   form_data: Record<string, TournamentEntryFieldValue>;
}

export interface TournamentEntrySubmitResponse {
   entry_id: number;
}

export interface TournamentEntrySubmitErrors {
   tournament_ids?: string[];
   submitter_email?: string[];
   submitter_phone?: string[];
   team_name?: string[];
   [key: `form_data.${string}`]: string[] | undefined;
}

// Tournament Games types
export interface GamePlayer {
   player: string | null;
   hcp: string | null;
   photo?: string | null;
   country?: string | null;
}

export interface TeamShirtColors {
   primary: string | null;
   secondary: string | null;
}

export interface GameTeam {
   teamName: string;
   score: string | null;
   hcp: string | null;
   players: GamePlayer[];
   shirtColors?: TeamShirtColors | null;
}

export interface GameScore {
   hasWinner: boolean;
   winnerIndex: 1 | 2 | null;
   nonWinnerIndex: 1 | 2 | null;
   gamePlayed: boolean;
   isTie: boolean;
   gameScore1: string | null;
   gameScore2: string | null;
}

export interface GameOfficials {
   umpire1: string | null;
   umpire2: string | null;
   referee: string | null;
}

export interface GameLinks {
   googleMapsLink: string | null;
   multiMarkerMap: string | null;
   livestreamLink: string | null;
}

export interface GameCards {
   redCard: string | null;
   yellowCard: string | null;
}

export interface GameGroundCoordinates {
   latitude: number;
   longitude: number;
}

export interface GameGround {
   name: string;
   address?: string | null;
   coordinates?: GameGroundCoordinates | null;
   directions_url?: string | null;
}

export interface Game {
   id: number;
   gameDate: string;
   time: string | null;
   club: ClubInfo | string | null;
   country: string | null;
   ground: string | GameGround | null;
   tournamentHandicap: string | null;
   teams: {
      team1: GameTeam;
      team2: GameTeam;
   };
   score: GameScore;
   officials: GameOfficials;
   links: GameLinks;
   cards?: GameCards;
}

export interface TournamentGamesResponse {
   data: Record<string, Game[]>;
   meta: {
      tournament_id: number;
      tournament_name: string;
      total_games: number;
      game_dates: string[];
   };
}

// Chukkers types
export interface ChukkerField {
   name: string;
   address: string | null;
   coordinates: {
      latitude: string;
      longitude: string;
   } | null;
}

export interface ChukkerDate {
   id: number;
   date: string;
   start_time: string;
   total_chukkers: number;
   field: ChukkerField | null;
   player_count: number;
   is_interested: boolean;
   chukkers_requested: number | null;
   guest_name: string | null;
   interest_count: number;
   registration_open: boolean;
   notes: string | null;
}

export interface ChukkerDatesResponse {
   data: ChukkerDate[];
   meta: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
      // True when the portal runs in demo mode and these sessions are
      // synthetic examples, not real records. Optional/absent = real data.
      is_demo?: boolean;
   };
}

export interface ChukkerStatus {
   c1: 'In' | 'Out' | null;
   c2: 'In' | 'Out' | null;
   c3: 'In' | 'Out' | null;
   c4: 'In' | 'Out' | null;
   c5: 'In' | 'Out' | null;
   c6: 'In' | 'Out' | null;
   c7: 'In' | 'Out' | null;
   c8: 'In' | 'Out' | null;
}

export interface ChukkerEntryColor {
   primary: string;
   secondary: string | null;
}

export interface ChukkerEntry {
   id: number;
   player: string;
   color: ChukkerEntryColor;
   start_time: string;
   team: 'A' | 'B';
   sort_order: number;
   chukkers: ChukkerStatus;
}

export interface ChukkerEntriesResponse {
   data: {
      date: string;
      total_chukkers: number;
      is_interested: boolean;
      entries: ChukkerEntry[];
      notes: string | null;
   };
   meta?: {
      // True when the session is synthetic demo data. Optional/absent = real data.
      is_demo?: boolean;
   };
}

// Gallery types
export type Gallery = {
   id: number;
   title: string;
   slug: string;
   /**Longer description to be used in the page that shows all images */
   description: string | null;
   /**Short description to be used in the gallery index page*/
   short_description: string | null;
   image_count: number;
   sort_order: number;
   thumbnail_url: string | null;
};

export type GalleryImage = {
   id: number;
   thumbnail_url: string;
   hd_url: string;
   alt_text: string | null;
   sort_order: number;
};

export type GalleryWithImages = Omit<Gallery, 'image_count' | 'sort_order'> & {
   images: GalleryImage[];
};

export type GalleriesResponse = { data: Gallery[] };
export type GalleryResponse = { data: GalleryWithImages };

// Leaderboard types
export type LeaderboardEntry = {
   rank: number;
   /**Already abbreviated server-side to first name + last initial. Render verbatim. */
   display_name: string;
   /**Distinct videos watched past the completion threshold. The ranking metric. */
   videos_watched: number;
   total_watch_seconds: number;
   /**
    * Flags the calling user's own row. The payload carries no user id or email, so
    * this is the only reliable way to find it - display_name is abbreviated and can
    * collide ("Sam R."), which is why we never match on it.
    */
   is_current_user: boolean;
};

export type LeaderboardMeta = {
   /**Completion bar currently in force. Always render this, never hardcode 70. */
   threshold_percent: number;
   /**Everyone who qualifies, not just those returned within the limit. */
   total_participants: number;
   /**The limit actually applied, after server-side clamping. */
   limit: number;
   current_user_ranked: boolean;
};

export type Leaderboard = {
   entries: LeaderboardEntry[];
   /**The caller's own row, resolved even when they fall outside the limit. */
   current_user: LeaderboardEntry | null;
   meta: LeaderboardMeta;
};

export type LeaderboardResponse = { leaderboard: Leaderboard };

// Brand-authored custom pages
// A page is a slug, a title and an ordered list of sections. Each section type maps to
// one component in components/sections; the API may send a type this app does not know
// yet, so unknown types are skipped rather than rendered (see sectionComponent).

export type LinkCard = {
   title: string;
   /** Plain text, not HTML. */
   description: string | null;
   image_url: string | null;
   /** Plain text. Null when the brand gave none - fall back to the card title. */
   image_alt: string | null;
   /** Absolute https URL, validated API-side. Always rendered rel="noopener noreferrer". */
   url: string;
   new_tab: boolean;
};

export type LinkCardsPayload = {
   columns: 2 | 3 | 4;
   cards: LinkCard[];
};

export type CustomPageSection = {
   /** Stable across saves - a section is updated in place, not recreated. Use as the
    * render key rather than the array index. */
   id: number;
   type: string;
   /** Plain text, rendered above the section body. Null when unset. */
   heading: string | null;
   /**
    * Sanitised HTML from a fixed vocabulary (h1-h6, p, strong, em, ul/ol/li, blockquote,
    * a, br) with no class or style attributes, purified API-side on save. Rendered above
    * the section body with v-html. No other field on a page is HTML.
    */
   description: string | null;
   payload: Record<string, unknown>;
};

export type CustomPage = {
   slug: string;
   title: string;
   sections: CustomPageSection[];
};

export type CustomPageResponse = { data: CustomPage };
